import { IQuery, IQueryMediator } from 'core-ts-types';
import * as _4 from 'lodash4';
import { ICommentsDatasource } from '../../../../comments/datasources/comments_datasource';
import { CommentServant } from '../../../../comments/servants/comment_servant';
import { ConflictingSuggestionsModel } from '../../blocked_suggestions/models/conflicting_suggestions_model';
import { PlanFeedbackCourse } from '../builders/plan_feedback_course_builder';
import { PlanFeedbackCourseGroup } from '../builders/plan_feedback_course_group_builder';
import { IPlanFeedbackTerm, PlanFeedbackTerm } from '../builders/plan_feedback_term_builder';
import { IFeedbackTermAndSuggestion, IFeedbackTransaction } from '../builders/plan_feedback_transaction_builder';
import { IPlanFeebackCourseGroupsDatasource } from '../datasources/plan_feedback_course_groups_datasource';
import { IPlanFeebackCoursesDatasource } from '../datasources/plan_feedback_courses_datasource';
import { IPlanFeedbackProgramSlotsDatasource } from '../datasources/plan_feedback_program_slots_datasource';
import { IPlanFeebackTermsDatasource } from '../datasources/plan_feedback_terms_datasource';
import { FeedbackSuggestedPlanModel } from '../models/feedback_suggested_plan_model';
import { PlanFeedbackServant } from '../servants/plan_feedback_servant';
import { PlanFeedbackTermFieldIdentifierStrategy } from '../strategies/plan_feedback_transaction_types_query_strategy';

export class AdvisorSentFeedbackDiffQuery implements IQuery {
  private __result: {
    termsAndSuggestions: IFeedbackTermAndSuggestion[];
    templateSuggestions: IFeedbackTransaction[];
    totalSuggestions: number;
    suggestionsExist: boolean;
    hasCommentOnly: boolean;
    acceptedAll: boolean;
    showAcknowledge: boolean;
  };

  constructor(
    QueryMediator: IQueryMediator,
    private ngFeedbackSuggestedPlanModel: FeedbackSuggestedPlanModel,
    private ngPlanFeebackTermsDatasource: IPlanFeebackTermsDatasource,
    private ngPlanFeebackCoursesDatasource: IPlanFeebackCoursesDatasource,
    private ngPlanFeebackCourseGroupsDatasource: IPlanFeebackCourseGroupsDatasource,
    private ngCommentsDatasource: ICommentsDatasource,
    private ngConflictingSuggestionsModel: ConflictingSuggestionsModel,
    private ngPlanFeedbackProgramSlotsDatasource: IPlanFeedbackProgramSlotsDatasource
  ) {
    QueryMediator.registerQuery(
      this,
      ngFeedbackSuggestedPlanModel,
      ngPlanFeebackTermsDatasource,
      ngPlanFeebackCoursesDatasource,
      ngPlanFeebackCourseGroupsDatasource,
      ngCommentsDatasource,
      ngPlanFeedbackProgramSlotsDatasource
    );
  }

  getFeedbackCourseGroupedByTerms(_conflictingIds: number[]) {
    const feedbackCoursesNotConflicting = _4.filter(
      this.ngPlanFeebackCoursesDatasource.metasource().derived(),
      (feedbackCourse) => {
        return !_4.includes(_conflictingIds, feedbackCourse.resolution);
      }
    );
    const feedbackCourses = _4.filter(feedbackCoursesNotConflicting, (feedbackCourse) =>
      PlanFeedbackServant.isValidFeedback(feedbackCourse)
    );
    return feedbackCourses.reduce((feedbackCourseGroupedByTerms, course) => {
      const fieldTerm = PlanFeedbackTermFieldIdentifierStrategy[course.diff].currentTermField;
      if (!feedbackCourseGroupedByTerms[course[fieldTerm]]) {
        feedbackCourseGroupedByTerms[course[fieldTerm]] = [];
      }
      feedbackCourseGroupedByTerms[course[fieldTerm]].push(course);
      return feedbackCourseGroupedByTerms;
    }, {} as Record<string, typeof feedbackCourses>);
  }

  getfeedbackCourseGroupsGroupedByTerms(_conflictingIds: number[]) {
    const feedbackCourseGroupsNotConflicting = _4.filter(
      this.ngPlanFeebackCourseGroupsDatasource.metasource().derived(),
      (feedbackCourse) => {
        return !_4.includes(_conflictingIds, feedbackCourse.resolution);
      }
    );
    const feedbackCourseGroups = _4.filter(feedbackCourseGroupsNotConflicting, (feedbackCourseGroup) =>
      PlanFeedbackServant.isValidFeedback(feedbackCourseGroup)
    );
    return _4.reduce(
      feedbackCourseGroups,
      (feedbackCourseGroupsGroupedByTerms, courseGroup) => {
        const fieldTerm = PlanFeedbackTermFieldIdentifierStrategy[courseGroup.diff].currentTermField;
        if (!feedbackCourseGroupsGroupedByTerms[courseGroup[fieldTerm]]) {
          feedbackCourseGroupsGroupedByTerms[courseGroup[fieldTerm]] = [];
        }
        feedbackCourseGroupsGroupedByTerms[courseGroup[fieldTerm]].push(courseGroup);
        return feedbackCourseGroupsGroupedByTerms;
      },
      {} as Record<string, typeof feedbackCourseGroups>
    );
  }

  getTermsAndSuggestions(_conflictingIds: number[]) {
    let terms: PlanFeedbackTerm[] = this.ngPlanFeebackTermsDatasource.values();
    terms = _4.sortBy(terms, (term) => moment(term.term.term_start_dt));

    const courseGroupedByTerms = this.getFeedbackCourseGroupedByTerms(_conflictingIds);
    const courseGroupsGroupedByTerms = this.getfeedbackCourseGroupsGroupedByTerms(_conflictingIds);

    return _4.reduce(
      terms,
      (termAndSuggestions: IFeedbackTermAndSuggestion[], feedbackTerm) => {
        const termId = feedbackTerm.term.id;
        if (!_4.isEmpty(courseGroupedByTerms[termId]) || !_4.isEmpty(courseGroupsGroupedByTerms[termId])) {
          const courseSuggestions = PlanFeedbackServant.getSuggestionsForCourses(courseGroupedByTerms[termId]);
          const courseGroupSuggestions = PlanFeedbackServant.getSuggestionsForCourseGroups(
            courseGroupsGroupedByTerms[termId]
          );

          termAndSuggestions.push({
            id: termId,
            nk: feedbackTerm.nk,
            term: feedbackTerm.term,
            suggestions: courseSuggestions.concat(courseGroupSuggestions)
          });
        }
        return termAndSuggestions;
      },
      []
    );
  }

  getTemplateSuggestions(_conflictingIds: number[]) {
    const programSlotTransactionsNotConflicting = _4.filter(
      this.ngPlanFeedbackProgramSlotsDatasource.values(),
      (feedbackCourse) => {
        return !_4.includes(_conflictingIds, feedbackCourse.resolution);
      }
    );
    const programSlotTransactions = programSlotTransactionsNotConflicting.filter((feedback) =>
      PlanFeedbackServant.isValidFeedback(feedback)
    );
    return PlanFeedbackServant.getSuggestionsForProgramSlots(programSlotTransactions);
  }

  run(): this {
    const conflictingIds: number[] = _4.get(
      this.ngConflictingSuggestionsModel.read(),
      'conflicting_resolution_ids',
      []
    );
    const termsAndSuggestions = this.getTermsAndSuggestions(conflictingIds);
    const templateSuggestions = this.getTemplateSuggestions(conflictingIds);
    const totalSuggestions = _4.sumBy(termsAndSuggestions, 'suggestions.length') + templateSuggestions.length;
    const suggestionsExist = totalSuggestions > 0;
    const suggestedPlan = this.ngFeedbackSuggestedPlanModel.read();
    const isStudentPlanConflicting = PlanFeedbackServant.isPlanCommitedAndBlocked(suggestedPlan);
    const hasCommentOnly = !suggestionsExist;
    const showAcknowledge = isStudentPlanConflicting && hasCommentOnly;
    const acceptedAll = !suggestionsExist;

    this.__result = {
      termsAndSuggestions,
      templateSuggestions,
      totalSuggestions,
      suggestionsExist,
      hasCommentOnly,
      acceptedAll,
      showAcknowledge
    };
    return this;
  }

  get result() {
    return this.__result;
  }
}

angular
  .module('capability-ng-academic-planner')
  .service('ngAPAdvisorSentFeedbackDiffQuery', AdvisorSentFeedbackDiffQuery);



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-academic-planner/src/suggestions/components/plan_feedback/queries/advisor_sent_feedback_diff_query.ts